/**
 * Formulario para crear/editar actividades del profesor
 * Permite definir tarea y objetivos
 */

import React, { useState, useEffect } from 'react';
import { teacherActivityService } from '../../services/teacherActivityService';
import { TeacherActivity, ActivityObjective } from '../../types';
import configService from '../../services/configService';
import { authService } from '../../services/authService';
import './TeacherActivityForm.css';

interface TeacherActivityFormProps {
  activity?: TeacherActivity | null; // Si se proporciona, es modo edición
  onSave: (activity: TeacherActivity) => void;
  onCancel: () => void;
}

const TeacherActivityForm: React.FC<TeacherActivityFormProps> = ({
  activity,
  onSave,
  onCancel,
}) => {
  const [taskDescription, setTaskDescription] = useState('');
  const [objectives, setObjectives] = useState<ActivityObjective[]>([]);
  const [classroomId, setClassroomId] = useState<number | null>(null);
  const [level, setLevel] = useState<string>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (activity) {
      // Modo edición: cargar datos existentes
      setTaskDescription(activity.task_description);
      setObjectives(activity.objectives || []);
      setClassroomId(activity.classroom_id || null);
      setLevel(activity.level || '');
    } else {
      // Modo creación: valores por defecto
      setTaskDescription('');
      setObjectives([]);
      // Usar context_id como valor por defecto para classroom_id
      const contextId = authService.getContextId();
      setClassroomId(contextId ? parseInt(contextId) : null);
      setLevel('');
    }
  }, [activity]);

  const handleAddObjective = () => {
    setObjectives([...objectives, { description: '' }]);
  };

  const handleRemoveObjective = (index: number) => {
    setObjectives(objectives.filter((_, i) => i !== index));
  };

  const handleObjectiveChange = (index: number, value: string) => {
    const updated = [...objectives];
    updated[index] = { description: value };
    setObjectives(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validaciones
    if (!taskDescription.trim()) {
      alert('Por favor, describe la tarea que deben realizar los estudiantes.');
      return;
    }

    if (objectives.length === 0) {
      alert('Por favor, agrega al menos un objetivo.');
      return;
    }

    // Validar que todos los objetivos tengan descripción
    for (const obj of objectives) {
      if (!obj.description.trim()) {
        alert('Todos los objetivos deben tener descripción.');
        return;
      }
    }

    // Validar que classroom_id esté presente (requerido para que los estudiantes vean la actividad)
    if (!classroomId || classroomId <= 0) {
      alert('Por favor, especifica el ID del aula. La actividad no será visible para los estudiantes sin este campo.');
      return;
    }

    setSaving(true);
    
    // Obtener email antes del try para que esté disponible en el catch
    const config = configService.getConfig();
    const email = config.email;
    if (!email) {
      alert('No se encontró el email del profesor.');
      setSaving(false);
      return;
    }

    try {
      let savedActivity: TeacherActivity | null;

      if (activity?.id) {
        // Modo edición
        savedActivity = await teacherActivityService.updateActivity(activity.id, {
          task_description: taskDescription.trim(),
          objectives: objectives,
          classroom_id: classroomId,
          level: level.trim() || null,
        });
      } else {
        // Modo creación
        console.log('[TeacherActivityForm] 📝 Creando nueva actividad con:', {
          email,
          taskDescription: taskDescription.trim(),
          objectivesCount: objectives.length,
          objectives,
          classroomId,
          level: level.trim() || null
        });
        savedActivity = await teacherActivityService.createActivity(
          email,
          taskDescription.trim(),
          objectives,
          classroomId,
          level.trim() || null
        );
      }

      if (savedActivity) {
        onSave(savedActivity);
      } else {
        console.error('[TeacherActivityForm] ❌ No se pudo guardar la actividad - savedActivity es null');
        alert('Error al guardar la actividad. Por favor, revisa la consola para más detalles e intenta de nuevo.');
      }
    } catch (error: any) {
      console.error('[TeacherActivityForm] ❌ Error al guardar actividad:', error);
      console.error('[TeacherActivityForm] 📋 Datos que se intentaron guardar:', {
        email,
        taskDescription: taskDescription.trim(),
        objectives,
        classroomId,
        level: level.trim() || null,
        objectivesCount: objectives.length
      });
      const errorMessage = error?.message || error?.toString() || 'Error desconocido';
      alert(`Error al guardar la actividad: ${errorMessage}. Por favor, revisa la consola para más detalles.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="teacher-activity-form">
      <div className="form-header">
        <div className="form-header-top">
          <button
            className="form-back-button"
            onClick={onCancel}
            aria-label="Volver"
            title="Volver"
          >
            <img 
              src="/assets/ui/iconos/Flecha_Atras.png" 
              alt="Volver"
              className="form-back-icon"
            />
          </button>
          <h2>{activity ? 'Editar Actividad' : 'Nueva Actividad'}</h2>
          <div style={{ width: '40px' }}></div> {/* Spacer para centrar el título */}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="activity-form">
        <div className="form-section">
          <label htmlFor="task-description">
            <strong>Descripción de la Tarea *</strong>
            <span className="field-hint">
              Describe qué deben hacer los estudiantes en esta actividad
            </span>
          </label>
          <textarea
            id="task-description"
            value={taskDescription}
            onChange={(e) => setTaskDescription(e.target.value)}
            placeholder="Ejemplo: Escribir un párrafo sobre tu familia usando al menos 5 verbos en presente..."
            rows={4}
            required
          />
        </div>

        <div className="form-section">
          <div className="section-header">
            <label>
              <strong>Objetivos de la Actividad *</strong>
              <span className="field-hint">
                Define qué deben lograr los estudiantes
              </span>
            </label>
            <button
              type="button"
              className="add-objective-button"
              onClick={handleAddObjective}
            >
              + Agregar Objetivo
            </button>
          </div>

          {objectives.length === 0 ? (
            <div className="empty-objectives">
              <p>No hay objetivos agregados. Haz click en "Agregar Objetivo" para comenzar.</p>
            </div>
          ) : (
            <div className="objectives-list">
              {objectives.map((objective, index) => (
                <div key={index} className="objective-item">
                  <div className="objective-header">
                    <span className="objective-number">Objetivo {index + 1}</span>
                    {objectives.length > 1 && (
                      <button
                        type="button"
                        className="remove-objective-button"
                        onClick={() => handleRemoveObjective(index)}
                        aria-label="Eliminar objetivo"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <div className="objective-fields">
                    <div className="objective-field">
                      <label htmlFor={`obj-desc-${index}`}>Descripción del objetivo *</label>
                      <input
                        id={`obj-desc-${index}`}
                        type="text"
                        value={objective.description}
                        onChange={(e) =>
                          handleObjectiveChange(index, e.target.value)
                        }
                        placeholder="Ejemplo: Usar al menos 5 verbos en presente"
                        required
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-section">
          <label htmlFor="classroom-id">
            <strong>ID del Aula *</strong>
            <span className="field-hint">
              Si no especificas un aula, la actividad no será visible para los estudiantes.
            </span>
          </label>
          <input
            id="classroom-id"
            type="number"
            value={classroomId || ''}
            onChange={(e) =>
              setClassroomId(e.target.value ? parseInt(e.target.value) : null)
            }
            placeholder="Ejemplo: 12345"
            min="1"
            required
          />
        </div>

        <div className="form-section">
          <label htmlFor="level">
            <strong>Nivel Académico (Opcional)</strong>
            <span className="field-hint">
              Nivel académico relacionado (A1.1, A1.2, A2.2, etc.)
            </span>
          </label>
          <input
            id="level"
            type="text"
            value={level}
            onChange={(e) => setLevel(e.target.value)}
            placeholder="Ejemplo: A1.1"
          />
        </div>

        <div className="form-actions">
          <button type="button" className="cancel-button" onClick={onCancel} disabled={saving}>
            Cancelar
          </button>
          <button type="submit" className="save-button" disabled={saving}>
            {saving ? 'Guardando...' : activity ? 'Actualizar Actividad' : 'Generar Actividad'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TeacherActivityForm;
